import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  OnDestroy,
  input,
  viewChild,
} from '@angular/core';

import * as echarts from 'echarts';
import { EChartsOption, GridComponentOption } from 'echarts';

export interface BarChartDataPoint<YValue> {
  value: YValue;
  faultNames?: string[] | null;
}

interface BarChartDataSet<YValue> {
  name: string;
  stackName?: string; // for "stacked" bar charts only
  color?: string; // if "undefined" default color set will be applied
  pattern?: {
    type: BarChartPatternType;
    color?: string;
  };
  data: (YValue | BarChartDataPoint<YValue>)[];
}

export type BarChartLegendPositionY = 'top' | 'bottom';
export type BarChartLegendPositionX = 'left' | 'right' | 'center';
export type BarChartPatternType = 'diagonal-line';
export type ChartPadding = Pick<GridComponentOption, 'left' | 'right' | 'top' | 'bottom'>;

export interface BarChartData<XValue, YValue> {
  showTooltip?: boolean;
  showLegend?: boolean;
  legendPosition?: [BarChartLegendPositionY, BarChartLegendPositionX];

  padding?: { [Key in keyof ChartPadding]?: number | string }; // pixels or % (echarts dynamic values will be used when not provided here)
  xValues: XValue[];
  yValues: BarChartDataSet<YValue> | BarChartDataSet<YValue>[];
  legend?: Record<string, unknown>;
}

const PATTERN_DIAGONAL_LINE = {
  dashArrayX: [1, 0],
  dashArrayY: [1, 3],
  rotation: -Math.PI / 4,
};

function resolvePattern(pattern: BarChartPatternType) {
  if (pattern === 'diagonal-line') {
    return PATTERN_DIAGONAL_LINE;
  }
  throw new Error(`Unknown bar-chart pattern: ${pattern}`);
}

function formatTooltip(rawParams: unknown): string {
  const params = rawParams as {
    seriesIndex?: number;
    marker: string;
    value: string;
    seriesName: string;
    name: string;
    data?: number | { value: number; faultNames?: string[] | null };
  };
  const seriesIndex = params.seriesIndex ?? 0;
  const solved = seriesIndex === 1 || seriesIndex === 3;
  const shutdown = seriesIndex === 0 || seriesIndex === 1;
  const marker = params.marker
    .replace(
      '<span ',
      `<span class="fault-history-marker-${shutdown ? 'shutdown' : 'warning'} fault-history-marker-${solved ? 'solved' : 'pending'}" `
    )
    .replace('<br/>', '');
  const faultNames = typeof params.data === 'object' && params.data ? (params.data.faultNames ?? []) : [];
  const faultName = faultNames.length ? `<div class="tooltip-fault-names">${faultNames.join(', ')}</div>` : '';
  return `${marker}<div class="tooltip">${params.seriesName}(${params.value}) ${params.name}
  <br/>
  ${faultName}
  </div>  `;
}

@Component({
  selector: 'gea-hrt-bar-chart',
  standalone: true,
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chart-container" #chart id="chart-container" title="" [style.width]="width()" [style.height]="height()"></div>
  `,
  host: { '(window:resize)': 'onResize()' },
})
export class BarChartComponent<XValue = string, YValue = number> implements AfterViewInit, OnDestroy {
  readonly title = input<string>();
  readonly width = input<string>();
  readonly height = input<string>();

  readonly data = input<BarChartData<XValue, YValue> | null>(null);

  constructor() {
    effect(() => {
      const data = this.data();
      if (this.chart && data) this.chart.setOption(this.buildChartOptions(data), { notMerge: true });
    });
  }

  private readonly chartRef = viewChild.required<ElementRef<HTMLDivElement>>('chart');
  private chart?: echarts.EChartsType;

  ngAfterViewInit(): void {
    this.chart = echarts.init(this.chartRef().nativeElement);
    const data = this.data();
    if (data) {
      this.chart.setOption(this.buildChartOptions(data), { notMerge: true });
    }
  }

  ngOnDestroy(): void {
    this.chart?.dispose();
  }

  onResize(): void {
    this.chart?.resize();
  }

  private buildChartOptions(data: BarChartData<XValue, YValue>): EChartsOption {
    const yValues = Array.isArray(data.yValues) ? data.yValues : [data.yValues];

    const options: EChartsOption = {
      title: {
        text: this.title(),
        show: !!this.title(),
      },
      legend: {
        show: data.showLegend,
        left: data.legendPosition?.[1] ?? 'left',
        top: data.legendPosition?.[0] ?? 'top',
        icon: 'circle',
        ...data.legend,
      },
      tooltip: {
        show: data.showTooltip,
        className: 'echarts-tooltip echarts-tooltip-fault-history',
        extraCssText: 'display: flex;',
        formatter: formatTooltip,
      },
      xAxis: {
        type: 'category',
        data: data.xValues as string[],
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        scale: false,
      },
      series: yValues.map((row) => ({
        type: 'bar' as const,
        name: row.name,
        color: row.color,
        stack: row.stackName,
        data: row.data as (number | BarChartDataPoint<number>)[],
        itemStyle: row.pattern ? { decal: { ...resolvePattern(row.pattern.type), color: row.pattern.color } } : undefined,
      })),
    };

    if (data.padding) {
      options.grid = { ...data.padding, left: data.padding.left ?? 25, right: data.padding.right ?? 0 };
    }

    return options;
  }
}
